import { FriendlyError, Markdown } from '@cibo/ui'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
} from '@mui/material'
import { Field, FieldArray, FieldProps } from 'formik'
import { equals, last } from 'ramda'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailEditorPropsByYear } from '../types'
import { EventComponentProps } from './EventDetailTypes'

interface EventDetailEditorPropsByYear extends Omit<DetailEditorPropsByYear, 'name'> {
  name: string
  /** Only required when simple=false (default) */
  EventTableHead?: () => ReactElement
  translationContext:
    | 'fertilizer'
    | 'amendment'
    | 'eventIrrigation'
    | 'tillage'
    | 'grazing'
    | 'liming'
    | 'naturalDisturbance'
  EventComponent: (props: EventComponentProps) => ReactElement
  defaultEvent: Record<any, any>
  modal?: boolean
  isImmutable: boolean
  /** When true renders the compact card-list layout instead of the table layout */
  simple?: boolean
}

export const EventDetailEditor = <I extends { events?: Record<any, any>[] | never[] } | undefined>({
  year,
  traitId,
  name,
  requirement,
  filterInputs,
  EventTableHead,
  EventComponent,
  translationContext,
  defaultEvent,
  fieldModels,
  modal,
  simple = false,
}: Omit<EventDetailEditorPropsByYear, 'isImmutable'>) => {
  const { t } = useTranslation(['eventEditor', '@cibo/ui'])
  const [editingEvent, setEditingEvent] = useState(false)
  const isAnyImmutable =
    fieldModels.filter(field => field.findDetail({ traitId, year })?.immutable).length > 0
  const isAddEventDisabled = isAnyImmutable || (modal ? !!editingEvent : false)

  return (
    <Field name={name} id={name}>
      {({
        field: { value: { events } = { events: [] } },
        form: { setFieldTouched, setFieldValue },
        meta: { initialValue, error },
      }: FieldProps<I>) => (
        <Stack spacing={1}>
          {!simple && (
            <Box paddingInline={2}>
              <Markdown>
                {t(`cta_${translationContext}`, {
                  year,
                  previousYear: year - 1,
                })}
              </Markdown>
            </Box>
          )}
          <FieldArray
            name={`${name}.events`}
            render={arrayHelpers => (
              <>
                <Collapse
                  in={!events?.length}
                  sx={simple ? { marginTop: '0 !important' } : undefined}
                >
                  <Stack
                    spacing={2}
                    sx={{ marginInline: 2, marginBlock: 1 }}
                    direction={simple ? 'column' : 'row'}
                  >
                    {!simple && (
                      <Button
                        onClick={() => arrayHelpers.push(defaultEvent)}
                        startIcon={<AddIcon />}
                        data-testid={`${name}-inline-add-event`}
                        disabled={isAnyImmutable}
                      >
                        {t(`addEvent_${traitId}`)}
                      </Button>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={isAnyImmutable}
                          name={`${name}.noEvents`}
                          checked={events === undefined ? false : !events.length}
                          onChange={(event, checked) => {
                            setFieldTouched(`${name}.events`, true, false)
                            if (checked) {
                              setFieldValue(`${name}.events`, [])
                            } else {
                              setFieldValue(`${name}.events`, undefined)
                            }
                          }}
                        />
                      }
                      label={t(`noEvents_${traitId}`)}
                    />
                    {simple && (
                      <Button
                        variant="outlined"
                        onClick={() => arrayHelpers.push(defaultEvent)}
                        startIcon={<AddIcon />}
                        data-testid={`${name}-inline-add-event`}
                        disabled={isAnyImmutable}
                      >
                        {t(`addEvent_${traitId}`)}
                      </Button>
                    )}
                  </Stack>
                </Collapse>

                <Collapse
                  in={!!events?.length}
                  sx={simple ? { marginTop: '0 !important' } : undefined}
                >
                  {simple ? (
                    <Stack spacing={2} sx={{ width: '100%' }}>
                      <Stack
                        direction="column"
                        spacing={0.5}
                        divider={<Divider />}
                        paddingBottom={1}
                      >
                        {events &&
                          events.length > 0 &&
                          // @ts-ignore events.map may be one of two types of function and typescript *hates* that ¯\_(ツ)_/¯
                          events.map((event: any, index: number) => (
                            <EventComponent
                              data-testid={`${name}-event-${index}`}
                              key={index}
                              name={`${name}.events.${index}`}
                              parentName={name}
                              year={year}
                              onPressRemove={() => arrayHelpers.remove(index)}
                              requirement={requirement}
                              filterInputs={filterInputs}
                              eventIndex={index}
                              onEditEvent={setEditingEvent}
                              editing={editingEvent}
                              isImmutable={isAnyImmutable}
                            />
                          ))}
                      </Stack>
                      {!(isAnyImmutable || editingEvent) && (
                        <Stack>
                          <Button
                            variant="outlined"
                            onClick={() => arrayHelpers.push(defaultEvent)}
                            startIcon={<AddIcon />}
                            data-testid={`${name}-footer-add-event`}
                            disabled={isAddEventDisabled}
                            sx={{ marginInline: 2, marginBottom: 2 }}
                          >
                            {t(`addEvent_${traitId}`)}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Box>
                        <Table aria-label={t(`tableLabel_${traitId}`) as string} size="small">
                          {EventTableHead && <EventTableHead />}

                          <TableBody>
                            {events &&
                              events.length > 0 &&
                              // @ts-ignore events.map may be one of two types of function and typescript *hates* that ¯\_(ツ)_/¯
                              events.map((event: any, index: number) => (
                                <EventComponent
                                  data-testid={`${name}-event-${index}`}
                                  key={index}
                                  name={`${name}.events.${index}`}
                                  parentName={name}
                                  year={year}
                                  onPressRemove={() => arrayHelpers.remove(index)}
                                  requirement={requirement}
                                  filterInputs={filterInputs}
                                  eventIndex={index}
                                  onEditEvent={setEditingEvent}
                                  editing={editingEvent}
                                  isImmutable={isAnyImmutable}
                                />
                              ))}
                          </TableBody>
                        </Table>
                      </Box>
                      <Box>
                        <Button
                          onClick={() => arrayHelpers.push(defaultEvent)}
                          startIcon={<AddIcon />}
                          data-testid={`${name}-footer-add-event`}
                          disabled={isAddEventDisabled}
                          sx={{ marginInline: 2, marginBottom: 2 }}
                        >
                          {t(`addEvent_${traitId}`)}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Collapse>
              </>
            )}
          />
          {/* We are immediately calling the validation on this, however we do not want to 
          present the error if it's a new event. */}
          {events &&
            !isAnyImmutable &&
            // @ts-ignore error is statically typed as a string but it isn't always a string
            error?.events?.length > 0 &&
            !equals(last(events), defaultEvent) && (
              <FriendlyError
                // @ts-ignore error is statically typed as a string but it isn't always a string
                message={t('eventError', { count: error?.events?.length || 0 }) || ''}
              />
            )}
        </Stack>
      )}
    </Field>
  )
}
